/**
 * Location detail screen (DNKADM-0051 U1/U3; tabs added DNKADM-0080) — one owned
 * `v2_location` as a **two-tab** destination under the shared location info header.
 * Routed at `/locations/:locationId` under `RequireAdmin`, as a layout route whose
 * child tabs swap the body:
 *  - **Hours of operation** ({@link LocationHoursTab}, default/first) at
 *    `/locations/:locationId/hours` (index → hours).
 *  - **Tax information** ({@link LocationTaxTab}) at `/locations/:locationId/tax`.
 *
 * Loads the location through `useResource` (`getLocation`), shows its name /
 * timezone / address / map link / status above the tab strip. The single `mapUrl`
 * (DNKAPI-0164) renders as a safe **Open in Maps** new-tab link — clickable **only**
 * when its scheme is `http`/`https` (an untrusted/other-scheme value renders as plain
 * text, never an active link). DNKADM-0081 transition: when `mapUrl` is absent but the
 * pre-0164 geo coords still arrive, it falls back to the old coord-built **Directions**
 * link; `mapUrl` is preferred whenever present. Absent map info simply omits (no empty
 * rows). A load failure shows error + Retry; a missing location (`404`) shows a clear
 * not-found note with a link back to the list. The tabs (and their `<Outlet/>` body)
 * render **only** when the location is `ready`.
 *
 * **Base-field editing (DNKADM-0286).** The header hosts an "Edit details" button that
 * opens the existing {@link LocationDialog} (name/timezone/address/…) against the
 * loaded record — the detail page is the single place to view AND edit; the list's
 * quick-edit trigger is retired. A save refetches the record.
 */
import { useCallback, useState } from 'react';
import {
  useParams,
  useLocation,
  useNavigate,
  Outlet,
  Link as RouterLink,
} from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Link as MuiLink,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { COURT_GREEN, Icon } from '@/theme';
import { useResource } from '@/hooks/useResource';
import { useToast } from '@/components/useToast';
import { getLocation, type V2Location } from '@/api/locations';
import { LocationDialog } from './LocationDialog';
import { ScreenError } from '@/components/ScreenError';

/**
 * Whether a freeform `mapUrl` is a safe-to-link `http`/`https` URL. `mapUrl` is
 * untrusted admin input (DNKAPI-0164) — only an `http(s)` value is rendered as an
 * active link; anything else (a `javascript:`/other scheme, or an unparseable string)
 * renders as plain text, never a clickable link.
 */
function isHttpUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * DNKADM-0081 transition fallback: a Google Maps Directions URL from the location's
 * pre-0164 geo coords (or null when none) — a `googlePlaceId` is preferred, otherwise
 * the lat/long pair. Used ONLY when `mapUrl` is absent; once DNKAPI-0164 is live the
 * coords stop arriving and this (with the deprecated coord fields) can be deleted.
 */
function directionsUrl(location: V2Location): string | null {
  if (location.googlePlaceId !== undefined) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      location.googlePlaceId,
    )}&query_place_id=${encodeURIComponent(location.googlePlaceId)}`;
  }
  if (location.latitude !== undefined && location.longitude !== undefined) {
    return `https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`;
  }
  return null;
}

/**
 * The location's map affordance: prefer the freeform `mapUrl` (DNKAPI-0164) — an
 * `http(s)` value renders as a safe new-tab "Open in Maps" link, a non-`http(s)`/
 * unparseable value renders as plain text (never an active link). When `mapUrl` is
 * absent, DNKADM-0081's transition fallback renders the pre-0164 coord-built
 * Directions link. Renders nothing when neither is available.
 */
function LocationMapLink({ location }: { location: V2Location }) {
  if (location.mapUrl !== undefined) {
    if (isHttpUrl(location.mapUrl)) {
      return (
        <MuiLink
          href={location.mapUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 13 }}
        >
          <Icon name="map" sx={{ fontSize: 18 }} />
          Open in Maps
        </MuiLink>
      );
    }
    // Untrusted non-http(s) value — show it as plain text, never a clickable link.
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary', wordBreak: 'break-all' }}>
        {location.mapUrl}
      </Typography>
    );
  }

  // Transition fallback (DNKADM-0081): no mapUrl yet, but the pre-0164 coords arrived.
  const fallback = directionsUrl(location);
  if (fallback) {
    return (
      <MuiLink
        href={fallback}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 13 }}
      >
        <Icon name="directions" sx={{ fontSize: 18 }} />
        Directions
      </MuiLink>
    );
  }
  return null;
}

/** The two tabs in display/route order. `path` is the route-map sub-tab segment. */
const TABS = [
  { path: 'hours', label: 'Hours of operation' },
  { path: 'tax', label: 'Tax information' },
] as const;

type TabPath = (typeof TABS)[number]['path'];

export function LocationDetail() {
  const { locationId = '' } = useParams<{ locationId: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // The Tax tab is the only non-default segment; everything else (incl. the index
  // redirect target) is the Hours tab.
  const activeTab: TabPath = pathname.endsWith('/tax') ? 'tax' : 'hours';

  const location = useResource(
    useCallback(() => getLocation(locationId), [locationId]),
    [locationId],
  );

  // Base-field editing in-context (DNKADM-0286): the same dialog the list's Add uses,
  // seeded with the loaded record; a save refetches the record (name/timezone/address
  // in the header — the tab bodies own their own resources).
  const showToast = useToast();
  const [editOpen, setEditOpen] = useState(false);

  return (
    <Box>
      <MuiLink
        component={RouterLink}
        to="/locations"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 13, mb: 2 }}
      >
        <Icon name="arrow_back" sx={{ fontSize: 18 }} />
        All locations
      </MuiLink>

      {location.status === 'loading' && (
        <Skeleton
          variant="rounded"
          height={120}
          role="status"
          aria-label="Loading location"
          sx={{ borderRadius: 3, maxWidth: 560 }}
        />
      )}

      {location.status === 'error' && (
        <ScreenError
          error={location.error}
          what="the location"
          onRetry={location.refetch}
          sx={{ maxWidth: 560 }}
          notFound={
            <Alert severity="warning" sx={{ maxWidth: 560 }}>
              That location wasn’t found. It may have been removed.{' '}
              <MuiLink component={RouterLink} to="/locations">
                Back to locations
              </MuiLink>
              .
            </Alert>
          }
        />
      )}

      {location.status === 'ready' && location.data && (
        <>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Typography variant="h6" component="h1">
              {location.data.name === '' ? location.data.id : location.data.name}
            </Typography>
            <Chip
              size="small"
              label={location.data.isActive ? 'Active' : 'Inactive'}
              color={location.data.isActive ? 'success' : 'default'}
              variant={location.data.isActive ? 'filled' : 'outlined'}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<Icon name="edit" />}
              onClick={() => setEditOpen(true)}
            >
              Edit details
            </Button>
          </Stack>
          <Stack spacing={0.25} sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Timezone: {location.data.timezone === '' ? '—' : location.data.timezone}
            </Typography>
            {location.data.address !== undefined && (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', whiteSpace: 'pre-line' }}
              >
                {location.data.address}
              </Typography>
            )}
            <LocationMapLink location={location.data} />
            {location.data.phone && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {location.data.phone}
              </Typography>
            )}
          </Stack>

          <Tabs
            value={activeTab}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            onChange={(_e, value: TabPath) => navigate(value)}
            aria-label="Location sections"
            sx={{
              mb: 3,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '& .MuiTabs-indicator': { bgcolor: COURT_GREEN },
              '& .Mui-selected': { color: `${COURT_GREEN} !important` },
            }}
          >
            {TABS.map((t) => (
              <Tab key={t.path} value={t.path} label={t.label} />
            ))}
          </Tabs>

          <Outlet />

          <LocationDialog
            open={editOpen}
            location={location.data}
            onClose={() => setEditOpen(false)}
            onSaved={() => {
              setEditOpen(false);
              location.refetch();
            }}
            showToast={showToast}
          />
        </>
      )}
    </Box>
  );
}
